import { NavLink } from 'react-router-dom';

import { HomeIcon, ComercialIcon, ClientesIcon, ComprasIcon, OsIcon, MoreIcon } from '@/components/icons';
import { cn } from '@/lib/cn';
import { useAuth } from '@/contexts/AuthContext';
import { PROFILES_ROUTING_ENABLED } from '@/lib/feature-flags';
import { useMyFeatureFlag } from '@/lib/feature-flags-store';
import type { ProfileSlug } from '@/lib/admin-types';

/**
 * MobileTabBar — barra inferior fixa no mobile.
 *
 * Visível apenas em telas < md (768px). No desktop usa Sidebar.
 *
 * FEAT-V2-037 Fase A (Cowork 2026-05-25):
 *   - Quando `PROFILES_ROUTING_ENABLED=false` (DEFAULT): 5 itens fixos
 *     (Home / Comercial / Clientes / OS / Mais) — comportamento legacy,
 *     todos os users veem o mesmo. Zero impacto até admin atribuir perfis
 *     em /admin/usuarios.
 *   - Quando flag ON: nav adapta por perfil do user:
 *       * comercial      → Home / Comercial / Clientes / Mais
 *       * administrativo → Home / Comercial / OS / Mais (atual menos Clientes
 *                          que vira "Mais" sub-item)
 *       * tecnico        → Home / OS / Mais
 *       * super-admin    → igual administrativo
 *   - Pra ligar em dev: `.env.local` com VITE_PROFILES_ROUTING_ENABLED=true.
 *
 * "Mais" leva pra `/mais` — gaveta com demais departamentos + Conta +
 * Sobre/Versão (BUG-V2-002, 2026-05-18).
 */

interface TabDef {
  to: string;
  label: string;
  Icon: React.FC<{ className?: string }>;
  end?: boolean;
}

// Tabs legacy — mostrados quando PROFILES_ROUTING_ENABLED=false OU quando
// user ainda não tem profile carregado (early loading defensivo).
const LEGACY_TABS: TabDef[] = [
  { to: '/', label: 'Home', Icon: HomeIcon, end: true },
  { to: '/menu/comercial', label: 'Comercial', Icon: ComercialIcon },
  // MOBILE-COMPLETO (M4): Clientes tinha DOIS endereços — `/menu/clientes` aqui
  // e `/admin/clientes-v2` no menu do computador e no "Mais". Agora os três
  // pontos de entrada levam ao mesmo; `/menu/clientes` continua redirecionando
  // (link antigo salvo não quebra).
  { to: '/admin/clientes-v2', label: 'Clientes', Icon: ClientesIcon },
  { to: '/menu/manutencao/agenda', label: 'Agenda', Icon: OsIcon },
  { to: '/mais', label: 'Mais', Icon: MoreIcon, end: true },
];

const TABS_BY_PROFILE: Record<ProfileSlug, TabDef[]> = {
  comercial: [
    { to: '/', label: 'Home', Icon: HomeIcon, end: true },
    { to: '/menu/comercial', label: 'Comercial', Icon: ComercialIcon },
    // FASE-C: apontava pra /menu/clientes, que só existia pra mostrar
    // "Placeholder. Implementação real virá…". A tela real é a lista v2.
    { to: '/admin/clientes-v2', label: 'Clientes', Icon: ClientesIcon },
    { to: '/mais', label: 'Mais', Icon: MoreIcon, end: true },
  ],
  // CO-1 (auditoria 2026-06-23): Compras incluído no administrativo (cobre
  // admin + super-admin). O conteúdo segue gated por COMPRAS_ENABLED na rota
  // (com a flag OFF o MenuPage mostra o aviso "Módulo ainda não liberado"). Default real de acesso
  // mobile é o drawer "Mais" (MaisPage), que é gated por COMPRAS_ENABLED — este
  // tab só aparece com PROFILES_ROUTING_ENABLED ON (não alteramos esse default).
  administrativo: [
    { to: '/', label: 'Home', Icon: HomeIcon, end: true },
    { to: '/menu/comercial', label: 'Comercial', Icon: ComercialIcon },
    { to: '/menu/manutencao/agenda', label: 'Agenda', Icon: OsIcon },
    { to: '/menu/compras/cotacoes', label: 'Compras', Icon: ComprasIcon },
    { to: '/mais', label: 'Mais', Icon: MoreIcon, end: true },
  ],
  tecnico: [
    { to: '/', label: 'Home', Icon: HomeIcon, end: true },
    { to: '/menu/manutencao/agenda', label: 'Agenda', Icon: OsIcon },
    { to: '/mais', label: 'Mais', Icon: MoreIcon, end: true },
  ],
  // Round D: comprador — Home · Cotações (D2) · Solicitar (D5) · Mais. Config
  // (tipos/fornecedores/alíquotas) fica em "Mais" → Catálogos → Compras.
  compras: [
    { to: '/', label: 'Home', Icon: HomeIcon, end: true },
    { to: '/menu/compras/cotacoes', label: 'Cotações', Icon: ComercialIcon },
    { to: '/menu/compras/solicitar', label: 'Solicitar', Icon: ClientesIcon },
    { to: '/mais', label: 'Mais', Icon: MoreIcon, end: true },
  ],
};

/**
 * Resolve tabs efetivos baseado em flag + perfil. Exportado pra testes.
 *
 * @internal — exportado apenas pra testes diretos; consumidores devem usar
 * `<MobileTabBar>` que envelopa esse cálculo.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function resolveTabs(opts: {
  enabled: boolean;
  profileSlug: ProfileSlug | null | undefined;
  isSuperAdmin: boolean;
}): TabDef[] {
  if (!opts.enabled) return LEGACY_TABS;
  // Super-admin sem perfil → segue layout administrativo (acesso amplo).
  if (opts.isSuperAdmin) return TABS_BY_PROFILE.administrativo;
  if (opts.profileSlug && opts.profileSlug in TABS_BY_PROFILE) {
    return TABS_BY_PROFILE[opts.profileSlug];
  }
  // Fallback defensivo (user sem perfil carregado ainda): legacy.
  return LEGACY_TABS;
}

export function MobileTabBar() {
  const { user } = useAuth();
  // CRM v2 (COMERCIAL-REBUILD): com a flag ON, a tab Comercial aponta pro
  // Funil novo. Mapeado aqui (não no resolveTabs) pra manter o resolver puro.
  const crmV2Enabled = useMyFeatureFlag('CRM_V2_ENABLED');
  const tabs = resolveTabs({
    enabled: PROFILES_ROUTING_ENABLED,
    profileSlug: user?.profile?.slug ?? null,
    isSuperAdmin: user?.isSuperAdmin === true,
  }).map((t) =>
    crmV2Enabled && t.to === '/menu/comercial' ? { ...t, to: '/admin/comercial/funil' } : t,
  );

  return (
    <nav
      className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-paper-warm dark:bg-paper-dark-warm border-t border-line dark:border-line-dark"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="flex items-stretch">
        {tabs.map((t) => (
          <NavLink
            key={t.label}
            to={t.to}
            end={t.end}
            className={({ isActive }) =>
              cn(
                'flex-1 flex flex-col items-center gap-1 py-2.5 px-1 text-xs font-medium transition-colors',
                isActive
                  ? 'text-red'
                  : 'text-ink-3 dark:text-ink-dark-3 hover:text-ink-1 dark:hover:text-ink-dark-1'
              )
            }
          >
            <t.Icon className="w-5 h-5" />
            <span>{t.label}</span>
          </NavLink>
        ))}
      </div>
    </nav>
  );
}
